'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';
import { RatingSheet } from './rating-sheet';
import type { Store } from '@/api/ahafood/get-store-fn';
import type { MyRating, OtherRating } from '@/api/use-get-rating';
import { useNavigation } from '@/components/navigation/navigation-context';

export interface RatingFilters {
  overall?: number;
  required_text?: boolean;
  required_image?: boolean;
}

interface RatingSheetContextType {
  openSheet: (
    store: Store,
    initialRatings?: (MyRating | OtherRating)[],
    filters?: RatingFilters
  ) => void;
  closeSheet: () => void;
  isOpen: boolean;
  currentStore: Store | null;
  initialRatings: (MyRating | OtherRating)[];
  filters: RatingFilters;
  updateFilters: (newFilters: RatingFilters) => void;
}

const RatingSheetContext = createContext<RatingSheetContextType | undefined>(
  undefined
);

export function RatingSheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [initialRatings, setInitialRatings] = useState<
    (MyRating | OtherRating)[]
  >([]);
  const [filters, setFilters] = useState<RatingFilters>({});
  const { setForceHidden } = useNavigation();

  const openSheet = useCallback(
    (
      store: Store,
      initialRatings: (MyRating | OtherRating)[] = [],
      filters: RatingFilters = {
        required_text: true,
      }
    ) => {
      setCurrentStore(store);
      setInitialRatings(initialRatings);
      setFilters(filters);
      setIsOpen(true);
      setForceHidden(true);
    },
    [setForceHidden]
  );

  const closeSheet = useCallback(() => {
    setIsOpen(false);
    setForceHidden(false);

    // Dispatch a custom event when sheet is closed
    const event = new Event('rating-sheet-closed');
    document.dispatchEvent(event);

    // We'll keep the current data during animation, then clear it
    setTimeout(() => {
      setCurrentStore(null);
      setInitialRatings([]);
      setFilters({});
    }, 300); // Adjust timing to match your animation duration
  }, [setForceHidden]);

  const updateFilters = useCallback((newFilters: RatingFilters) => {
    setFilters(newFilters);
  }, []);

  return (
    <RatingSheetContext.Provider
      value={{
        openSheet,
        closeSheet,
        isOpen,
        currentStore,
        initialRatings,
        filters,
        updateFilters,
      }}
    >
      {children}
      <RatingSheet
        store={currentStore}
        initialRatings={initialRatings}
        filters={filters}
        onClose={closeSheet}
        onFiltersChange={updateFilters}
      />
    </RatingSheetContext.Provider>
  );
}

export function useRatingSheet() {
  const context = useContext(RatingSheetContext);
  if (context === undefined) {
    throw new Error('useRatingSheet must be used within a RatingSheetProvider');
  }
  return context;
}
